<template>
    <div class="rules content-wrapper">
        <div class="title-wrapper">
            <span class="bracket-wrapper">
                <img class="bracket" src="~@/assets/celtic_bracket.png" />
            </span>
            <h1 class="title">
                How to Play
            </h1>
            <span class="bracket-wrapper left">
                <img class="bracket flipped" src="~@/assets/celtic_bracket.png" />
            </span>
        </div>
        <div class="content" ref="rulesContent">
            <div class="quote">
                "In War Chest you take on the role of medieval battlefield commanders,
                vying to gain control of tactical battlefield locations."
                <div class="quote-attribution">
                    - From the
                    <a class="link" :href="`${assetsUrl}/warchest_rules.pdf`" target="_blank">
                        official rulebook
                    </a>
                </div>
            </div>
            <div class="separator">
                <img class="bracket" src="~@/assets/celtic_bracket.png" />
                <span class="section-title">Setup</span>
                <img class="bracket flipped" src="~@/assets/celtic_bracket.png" />
            </div>
            <div>
                <ul class="items-list">
                    <li>
                        Each player is dealt 4 types of units (or 3 types in a 4-player game).

                        <div class="visual">
                            <unit-image :unitType="UNIT_TYPE_ARCHER" classes="example-unit" />
                            <unit-image :unitType="UNIT_TYPE_ROYAL_GUARD" classes="example-unit" />
                            <unit-image :unitType="UNIT_TYPE_MARSHAL" classes="example-unit" />
                            <unit-image :unitType="UNIT_TYPE_KNIGHT" classes="example-unit" />
                        </div>
                    </li>
                    <li>
                        Two units of each type, plus the Royal Coin, are placed into the player's bag.

                        <div class="visual">
                            <div class="example-unit-group">
                                <unit-image :unitType="UNIT_TYPE_ARCHER" classes="example-unit" />
                                <unit-image :unitType="UNIT_TYPE_ARCHER" classes="example-unit" />
                            </div>
                            <div class="example-unit-group">
                                <unit-image :unitType="UNIT_TYPE_ROYAL_GUARD" classes="example-unit" />
                                <unit-image :unitType="UNIT_TYPE_ROYAL_GUARD" classes="example-unit" />
                            </div>
                            <div class="example-unit-group">
                                <unit-image :unitType="UNIT_TYPE_MARSHAL" classes="example-unit" />
                                <unit-image :unitType="UNIT_TYPE_MARSHAL" classes="example-unit" />
                            </div>
                            <div class="example-unit-group">
                                <unit-image :unitType="UNIT_TYPE_KNIGHT" classes="example-unit" />
                                <unit-image :unitType="UNIT_TYPE_KNIGHT" classes="example-unit" />
                            </div>
                            <unit-image :unitType="UNIT_TYPE_ROYAL_COIN_WHITE" classes="example-unit" />
                            <div class="arrow right">
                                <fa-icon class="arrow-icon" icon="arrow-down" />
                            </div>
                            <div class="bag-container">
                                <img class="bag" src="../../assets/bag.png" />
                            </div>
                        </div>
                    </li>
                    <li>
                        Initiative is randomly assigned to one of the players.
                        <div class="visual">
                            <div class="initiative-wrapper">
                                <span class="initiative-text">
                                    Initiative
                                </span>
                                <span class="initiative-marker">
                                    <unit-image :unitType="UNIT_TYPE_ROYAL_COIN_BLACK" classes="example-unit" />
                                </span>
                            </div>
                        </div>
                    </li>
                    <li>
                        Each player starts with two bases under their control
                        (or 3 bases for each team in a 4-player game).
                        <div class="visual">
                            <div class="control-marker-group">
                                <div class="control-marker-images">
                                    <img class="control-marker"
                                        :src="getAssetUrl('../../assets/control_marker_white.png')" />
                                    <img class="control-marker"
                                        :src="getAssetUrl('../../assets/control_marker_white.png')" />
                                </div>
                                <div class="caption">
                                    White control markers
                                </div>
                            </div>
                            <div class="control-marker-group">
                                <div class="control-marker-images">
                                    <img class="control-marker"
                                        :src="getAssetUrl('../../assets/control_marker_black.png')" />
                                    <img class="control-marker"
                                        :src="getAssetUrl('../../assets/control_marker_black.png')" />
                                </div>
                                <div class="caption">
                                    Black control markers
                                </div>
                            </div>
                        </div>
                    </li>
                    <li>
                        In a 2-player game, the outer dark tiles are not used.
                        <div class="visual">
                            <div class="tiles">
                                <div class="tile-wrapper">
                                    <div class="tile-image-wrapper">
                                        <img class="rules-tile" :src="getAssetUrl('../../assets/tile.png')" />
                                    </div>
                                    <div class="caption">
                                        2-player tiles
                                    </div>
                                </div>
                                <div class="tile-wrapper">
                                    <div class="tile-image-wrapper">
                                        <img class="rules-tile" :src="getAssetUrl('../../assets/dark_tile.png')" />
                                    </div>
                                    <div class="caption">
                                        4-player tiles
                                    </div>
                                </div>
                            </div>
                        </div>
                    </li>
                </ul>
            </div>
            <div class="separator">
                <img class="bracket" src="~@/assets/celtic_bracket.png" />
                <span class="section-title">Gameplay</span>
                <img class="bracket flipped" src="~@/assets/celtic_bracket.png" />
            </div>
            <div>
                <ul class="items-list">
                    <li>
                        The objective of the game is to take control of 6 bases (or 8 bases in a 4-player game).
                        <div class="visual">
                            <div class="tiles">
                                <div class="tile-wrapper">
                                    <div class="tile-image-wrapper">
                                        <img class="rules-tile has-control-base" :src="getAssetUrl('../../assets/tile.png')" />
                                        <img class="control-base" :src="controlBaseImage" />
                                    </div>
                                    <div class="caption">
                                        Control base
                                    </div>
                                </div>
                            </div>
                        </div>
                    </li>
                    <li>
                        Each player draws 3 units out of the the bag at the start of the round
                        and keeps them hidden from the other player(s).

                        <div class="visual">
                            <div class="example-unit-group">
                                <img class="example-unit" src="../../assets/coin_back.png" />
                            </div>
                            <div class="example-unit-group">
                                <img class="example-unit" src="../../assets/coin_back.png" />
                            </div>
                            <div class="example-unit-group last-group">
                               <img class="example-unit" src="../../assets/coin_back.png" />
                            </div>
                            <div class="arrow left">
                                <fa-icon class="arrow-icon" icon="arrow-down" />
                            </div>
                            <div class="bag-container">
                                <img class="bag" src="../../assets/bag.png" />
                            </div>
                        </div>
                    </li>
                    <li>
                        If at the time of drawing there aren't enough units in the bag, it will be refilled from the
                        discard pile and units will continue to be drawn until you have 3 in your hand.
                    </li>
                    <li>
                        On your turn, use a unit from your hand to perform one of the actions listed below.
                    </li>
                </ul>
            </div>
            <div class="separator">
                <img class="bracket" src="~@/assets/celtic_bracket.png" />
                <span class="section-title">Actions</span>
                <img class="bracket flipped" src="~@/assets/celtic_bracket.png" />
            </div>
            <div>
                <ul class="items-list actions-list">
                    <li>
                        <div class="item-content">
                            <div class="btn-icon-group">
                                <fa-icon class="action-icon" icon="long-arrow-alt-down" />
                                <icon class="action-icon-secondary" icon="knight-helmet" />
                            </div>
                            <div class="text-wrapper">
                                <div class="text">
                                    <strong>Deploy</strong> - place a unit from your hand onto an empty base
                                    you control.
                                </div>
                                <div class="special-note">
                                    The Scout can be deployed onto any empty tile
                                    adjacent to a friendly unit.
                                </div>
                            </div>
                        </div>
                    </li>
                    <li>
                        <div class="item-content">
                            <div class="btn-icon-group bolster">
                                <fa-icon class="action-icon" icon="shield" />
                                <fa-icon class="action-icon plus-icon" icon="plus" />
                            </div>
                            <div class="text-wrapper">
                                <div class="text">
                                    <strong>Bolster</strong> - place a unit from your hand onto another unit
                                    of the same type that's already on the board.
                                </div>
                                <div class="special-note">
                                    Bolstering is the action of stacking units
                                    of the same type on top of each other to make them harder to
                                    eliminate and, in some cases, enable them to attack other special units.
                                </div>
                            </div>
                        </div>
                    </li>
                    <li>
                        <div class="item-content">
                            <div class="btn-icon">
                                <fa-icon icon="crown" />
                            </div>
                            <div class="text-wrapper">
                                <div class="text">
                                    <strong>Claim Initiative</strong> - become the starting player in the
                                    next round.
                                </div>
                                <div class="special-note">
                                    You can only claim initiative if it currently belongs
                                    to your opponent(s) and has yet to be taken this round.
                                </div>
                            </div>
                        </div>
                    </li>
                    <li>
                        <div class="item-content">
                            <div class="btn-icon">
                                <fa-icon icon="coins" />
                            </div>
                            <div class="text-wrapper">
                                <div class="text">
                                    <strong>Recruit</strong> - gain any unit from the supply.
                                </div>
                                <div class="special-note">
                                    A recruited unit goes into your discard pile, and will go into your
                                    bag when it's time to refill it.
                                </div>
                            </div>
                        </div>
                    </li>
                    <li>
                        <div class="item-content">
                            <div class="btn-icon">
                                <fa-icon class="flipped" icon="reply" />
                            </div>
                            <div class="text-wrapper">
                                <div class="text">
                                    <strong>Pass</strong> - end your turn.
                                </div>
                            </div>
                        </div>
                    </li>
                    <li>
                        <div class="item-content">
                            <div class="btn-icon">
                                <fa-icon icon="horse-head" />
                            </div>
                            <div class="text-wrapper">
                                <div class="text">
                                    <strong>Move</strong> - move a unit of the same type
                                    to another space on the board within range.
                                </div>
                            </div>
                        </div>
                    </li>
                    <li>
                        <div class="item-content">
                            <div class="btn-icon-group attack">
                                <icon class="action-icon" icon="sword" />
                            </div>
                            <div class="text-wrapper">
                                <div class="text">
                                    <strong>Attack</strong> - eliminate an opponent's unit within range.
                                </div>
                                <div class="special-note">
                                    When a unit on the board is attacked, a coin from its stack
                                    is removed from the game.
                                </div>
                            </div>
                        </div>
                    </li>
                    <li>
                        <div class="item-content">
                            <div class="btn-icon">
                                <fa-icon icon="flag" />
                            </div>
                            <div class="text-wrapper">
                                <div class="text">
                                    <strong>Control</strong> - Take control of a neutral or opponent-controlled
                                    base that your matching unit is on.
                                </div>
                                <div class="special-note">
                                    One of your control markers will be placed on your unit's location.
                                </div>
                            </div>
                        </div>
                    </li>
                    <li>
                        <div class="item-content">
                            <span class="action-icon-placeholder"></span>
                            <div class="text-wrapper">
                                <div class="text">
                                    <strong>Tactic</strong> - use a unit's tactic ability.
                                </div>
                                <div class="special-note">
                                    For most units, the tactic ability is built into their
                                    move, attack, or deploy
                                    actions. For the Ensign and Marshal,
                                    first select them in your hand, and then select the friendly
                                    unit on the board you wish to activate.
                                </div>
                            </div>
                        </div>

                    </li>
                    <li>
                        <div class="item-content">
                            <icon class="btn-icon horseshoe" icon="horseshoe" />
                            <div class="text-wrapper">
                                <div class="text">
                                    <strong>Attack via position</strong> - choose
                                    where the unit will end its movement when it attacks
                                    with its <strong>tactic</strong> ability.
                                </div>
                            </div>
                        </div>
                    </li>
                    <li>
                        <div class="item-content">
                            <div class="btn-icon-group attack sacrifice-attack">
                                <icon class="action-icon" icon="sword" />
                                <fa-icon class="action-icon drop" icon="tint" />
                            </div>
                            <div class="text-wrapper">
                                <div class="text">
                                    <strong>Sacrifice</strong> - eliminate an opponent's unit within range,
                                    but lose a coin from the attacking unit.
                                </div>
                                <div class="special-note">
                                    *Nobility expansion only.
                                </div>
                            </div>
                        </div>
                    </li>
                    <li>
                        <div class="item-content">
                            <div class="btn-icon">
                                <fa-icon class="reinforce" icon="heart" />
                            </div>
                            <div class="text-wrapper">
                                <div class="text">
                                    <strong>Reinforce</strong> - Return an eliminated unit back to the supply.
                                </div>
                                <div class="special-note">
                                    *Nobility expansion only.
                                </div>
                            </div>
                        </div>
                    </li>
                    <li>
                        <div class="item-content">
                            <div class="btn-icon-group build-fort">
                                <fa-icon class="action-icon plus-icon" icon="plus" />
                                <icon class="action-icon fort-icon" icon="fort" />
                            </div>
                            <div class="text-wrapper">
                                <div class="text">
                                    <strong>Build Fortification</strong> - build a Fortification on the current location
                                </div>
                                <div class="special-note">
                                    *Siege expansion only.
                                </div>
                            </div>
                        </div>
                    </li>
                    <li>
                        <div class="item-content">
                            <div class="btn-icon-group attack-fort">
                                <icon class="action-icon fort-icon" icon="fort" />
                                <span class="action-icon-secondary slash-wrapper">
                                    <span class="action-icon-slash" />
                                    <span class="action-icon-slash" />
                                </span>
                            </div>
                            <div class="text-wrapper">
                                <div class="text">
                                    <strong>Attack Fortification</strong> - destroy a neutral or
                                    opponent-controlled Fortification
                                </div>
                                <div class="special-note">
                                    *Siege expansion only.
                                </div>
                            </div>
                        </div>
                    </li>
                    <li>
                        <div class="item-content">
                            <div class="btn-icon">
                                <icon class="action-icon poison" icon="poison" />
                            </div>
                            <div class="text-wrapper">
                                <div class="text">
                                    <strong>Poison</strong> - Give the target unit a Poison Counter. That unit may not maneuver
                                     (move, control, attack, or use its tactic) or be bolstered.
                                     However, it may be manuevered by other units or decrees.
                                </div>
                                <div class="special-note">
                                    *Nightfall expansion only.
                                </div>
                            </div>
                        </div>
                    </li>
					<li>
                        <div class="item-content">
                            <div class="btn-icon">
                                <ArrowTurnUp class="action-icon return-decoy-coin-icon arrow-up-icon" />
                            </div>
                            <div class="text-wrapper">
                                <div class="text">
                                    <strong>Return Decoy Coin</strong> - Return the Decoy Coin in your hand back to its original owner.
                                </div>
                                <div class="special-note">
                                    *Nightfall expansion only.
                                </div>
                            </div>
                        </div>
                    </li>
                    <li>
                        <div class="item-content">
                            <div class="btn-icon">
								<ShockIcon class="icon expansion-icon shock-tactics" />
                            </div>
                            <div class="text-wrapper">
                                <div class="text">
                                    <strong>Shock</strong> - When a unit <strong>shocks</strong> a target unit,
                                     take all of the target unit's coins from
                                     the board and place them in its owner's discard pile.
                                </div>
                                <div class="special-note">
                                    *Shock Tactics expansion only.
                                </div>
                            </div>
                        </div>
                    </li>
					<li>
                        <div class="item-content">
                            <div class="btn-icon-group unbolster">
                                <fa-icon class="action-icon" icon="shield" />
                                <fa-icon class="action-icon minus-icon" icon="minus" />
                            </div>
                            <div class="text-wrapper">
                                <div class="text">
                                    <strong>Unbolster</strong> - remove a bolstered coin from a friendly unit on the board
									and return it to your discard pile.
                                </div>
                            </div>
                        </div>
                    </li>
					<li>
                        <div class="item-content">
                            <div class="btn-icon-group maneuver">
                                <fa-icon class="action-icon" icon="person-running" />
                            </div>
                            <div class="text-wrapper">
                                <div class="text">
                                    <strong>Maneuver</strong> - perform a <strong>maneuver</strong> with a friendly unit.
                                </div>
                            </div>
                        </div>
                    </li>
					<li>
                        <div class="item-content">
                            <div class="btn-icon-group maneuver">
                                <fa-icon class="action-icon" icon="person-running" />
								<ShockIcon class="action-icon shock-icon" />
                            </div>
                            <div class="text-wrapper">
                                <div class="text">
                                    <strong>Shocked Maneuver</strong> - perform a <strong>maneuver</strong> with a friendly unit, then <strong>shock</strong> it.
                                </div>
                            </div>
                        </div>
                    </li>
                </ul>
            </div>
            <div class="actions-gallery">
                <div class="actions-gallery-item" v-for="actionType in actionTypes" :key="`action-${actionType}`">
                    <div class="action-img-wrapper">
                        <img class="action-img" :src="getAssetUrl(`../../assets/action_${actionType}.png`)" />
                    </div>
                    <span class="caption">{{ typeToLabel(actionType) }} </span>
                </div>
            </div>
            <div class="separator">
                <img class="bracket" src="~@/assets/celtic_bracket.png" />
                <span class="section-title">Video Tutorial</span>
                <img class="bracket flipped" src="~@/assets/celtic_bracket.png" />
            </div>
            <div class="video">
                <iframe
                    src="https://www.youtube.com/embed/f96QtUCV0iM"
                    frameborder="0"
                    allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                    allowfullscreen
                ></iframe>
            </div>
            <div class="separator">
                <img class="bracket" src="~@/assets/celtic_bracket.png" />
                <span class="section-title">Nobility Expansion</span>
                <img class="bracket flipped" src="~@/assets/celtic_bracket.png" />
            </div>
            <div class="link-wrapper">
                <a class="link" :href="`${assetsUrl}/nobility_expansion_rules.pdf`" target="_blank">
                    [Click here for the official rules]
                </a>
            </div>
            <div>
                <ul class="items-list">
                    <li>
                        <div class="item-content">
                            The Nobility expansion adds 4 new units:
                        </div>
                        <div class="unit-types">
                            <div
                                class="unit-type"
                                v-for="(unitType) in nobilityUnits"
                                :key="`nobility-unit-type-${unitType}`"
                            >
                                <img
                                    class="unit-coin"
                                    :src="getAssetUrl(`@/assets/units/${unitType.toLowerCase().replace('_nobility', '')}.png`)"
                                />
                                <div class="unit-caption">
                                    {{typeToLabel(unitType)}}
                                </div>
                            </div>
                        </div>
                    </li>
                    <li>
                        <div class="item-content">
                            And 7 new Decrees:
                        </div>
                        <div class="decrees">
                            <div class="decree-name">Enlist</div>
                            <div class="decree-name">Guard</div>
                            <div class="decree-name">March</div>
                            <div class="decree-name">Redeploy</div>
                            <div class="decree-name">Reinforce</div>
                            <div class="decree-name">Sacrifice</div>
                            <div class="decree-name">Spy</div>
                        </div>
                    </li>
                    <li>
                        At the start of the game, 3 Decrees are dealt at random.
                    </li>
                    <li>
                        Decrees represent special actions that give players an advantage during the game.
                    </li>
                    <li>
                        Decrees are activated using the Royal Coin.
                    </li>
                    <li>
                        <div class="item-content">
                            <div class="text-wrapper">
                                <div class="text">
                                    Each player (or team in a 4-player game) may activate each Decree once per game.
                                </div>
                                <div class="special-note">
                                    When the Earl controls a base, it may activate a Decree for free.
                                </div>
                            </div>
                        </div>
                    </li>
                </ul>
            </div>
            <div class="separator">
                <img class="bracket" src="~@/assets/celtic_bracket.png" />
                <span class="section-title">Siege Expansion</span>
                <img class="bracket flipped" src="~@/assets/celtic_bracket.png" />
            </div>
            <div class="link-wrapper">
                <a class="link" :href="`${assetsUrl}/siege_expansion_rules.pdf`" target="_blank">
                    [Click here for the official rules]
                </a>
            </div>
            <div>
                <ul class="items-list">
                    <li>
                        <div class="item-content">
                            The Siege expansion adds 4 new units:
                        </div>
                        <div class="unit-types">
                            <div
                                class="unit-type"
                                v-for="(unitType) in siegeUnits"
                                :key="`siege-unit-type-${unitType}`"
                            >
                                <img
                                    class="unit-coin"
                                    :src="getAssetUrl(`@/assets/units/${unitType.toLowerCase()}.png`)"
                                />
                                <div class="unit-caption">
                                    {{typeToLabel(unitType)}}
                                </div>
                            </div>
                        </div>
                    </li>
                    <li>
                        It also adds a new type of structure called Fortification.
                    </li>
                    <li>
                        <div class="item-content">
                            At the start of the game, a layout card will be chosen at random to determine
                            the starting location of 4 Fortifications:
                        </div>
                        <div class="fort-layout-example">
                            <fort-layout :forts="fortLayoutExample" />
                        </div>
                    </li>
                    <li>
                        Neutral and opponent-controlled Fortifications may be attacked like normal units.
                    </li>
                    <li>
                        If a unit is inside a Fortification, the Fortification must be attacked
                        first before the unit can be attacked.
                    </li>
                    <li>
                        Units may not move into opponent-controlled Fortifications.
                    </li>
                    <li>
                        Units may move into - but not through - neutral and friendly Fortifications.
                    </li>
                    <li>
                        The Siege Tower, Trebuchet, and War Wagon must be bolstered in order to use
                        their <strong>Siege Tactics</strong>.
                    </li>
                </ul>
            </div>
            <div class="separator" ref="nightfallExpansionSection">
                <img class="bracket" src="~@/assets/celtic_bracket.png" />
                <span class="section-title">Nightfall Expansion</span>
                <img class="bracket flipped" src="~@/assets/celtic_bracket.png" />
            </div>
            <div class="link-wrapper">
                <a class="link" :href="`${assetsUrl}/nightfall_expansion_rules.pdf`" target="_blank">
                    [Click here for the official rules]
                </a>
            </div>
            <div>
                <ul class="items-list">
                    <li>
                        <div class="item-content">
                            The Nightfall expansion adds 4 new units:
                        </div>
                        <div class="unit-types">
                            <div
                                class="unit-type"
                                v-for="(unitType) in nightfallUnits"
                                :key="`nightfall-unit-type-${unitType}`"
                            >
                                <img
                                    class="unit-coin"
                                    :src="getAssetUrl(`@/assets/units/${unitType.toLowerCase()}.png`)"
                                />
                                <div class="unit-caption">
                                    {{typeToLabel(unitType)}}
                                </div>
                            </div>
                        </div>
                    </li>
                    <li>
                        It also adds two new mechanics:
                        <ul class="items-list">
                            <li>
                                Decoy Coins
                            </li>
                            <li>
                                Poison Counters
                            </li>
                        </ul>
                    </li>
                    <li>
                        <ul>
                            <li>
                                <div class="subtitle">
                                    <unit-image :unitType="UNIT_TYPE_DECOY_COIN_INFILTRATOR" classes="coin-image" />
                                     <span class="subtitle-label"><strong>Infiltrator Decoy Coin</strong></span>:
                                </div>
                                <div class="description">
                                    When the Infiltrator <strong>controls a base</strong>, its owner may discard the Infiltrator's
                                     Decoy Coin from their own supply into their <strong>opponent's</strong> discard pile.
                                </div>
                                  <div class="special-note">
                                    A Decoy Coin in hand may only be used to Recruit, Claim Initiative, Pass,
                                     or be returned to its owner.
                                </div>
                            </li>
                        </ul>
                    </li>
                    <li>
                        <ul>
                            <li>
                                <div class="subtitle">
                                    <unit-image :unitType="UNIT_TYPE_DECOY_COIN_SKIRMISHER" classes="coin-image" />
                                    <span class="subtitle-label"><strong>Skirmisher Decoy Coin</strong></span>:
                                </div>
                                <div class="description">
                                    When the Skirmisher <strong>is attacked</strong>, its owner may discard the Skirmisher's
                                     Decoy Coin from their own supply into their <strong>opponent's</strong> discard pile,
                                     instead of removing a coin from the board.
                                </div>
                                  <div class="special-note">
                                    A Decoy Coin in hand may only be used to Recruit, Claim Initiative, Pass,
                                     or be returned to its owner.
                                </div>
                            </li>
                        </ul>
                    </li>
                    <li>
                        <ul>
                            <li>
                                <div class="subtitle">
                                    <img class="token-image" src="~@/assets/poison_token_assassin.png">
                                    <img class="token-image" src="~@/assets/poison_token_saboteur.png">
                                    <span> <strong>Poison Counters (Assassin/Saboteur)</strong></span>:
                                </div>
                                <div class="description">
                                    The Assassin and Saboteur each start with 1 Poison Counter in the supply.
                                     They may use their tactic ability to poison an opponent's unit.
                                </div>
                                <div class="description">
                                    A poisoned unit receives a Poison Counter, and may not maneuver
                                     (move, control, attack, or use its tactic) or be bolstered.
                                </div>
                                <div class="description">
                                    A poisoned unit may be maneuvered or bolstered by <i>other units</i> or <i>decrees</i>.
                                </div>
                                <div class="description">
                                   The Assassin and Saboteur may transfer their Poison Counter from one target unit to another.
                                </div>
                                <div class="special-note">
                                    To remove a Poison Counter from a unit on the board,
                                     a player must spend a matching unit coin from their hand.
                                </div>
                            </li>
                        </ul>
                    </li>
                </ul>
            </div>
            <div class="separator" ref="shockTacticsExpansionSection">
                <img class="bracket" src="~@/assets/celtic_bracket.png" />
                <span class="section-title">Shock Tactics Expansion</span>
                <img class="bracket flipped" src="~@/assets/celtic_bracket.png" />
            </div>
            <div>
                <ul class="items-list">
                    <li>
                        <div class="item-content">
                            The Shock Tactics expansion adds 4 new units
                             (<span class="link" @click="onToggleShockTacticsModal">unit cards</span>):
                        </div>
                        <div class="unit-types">
                            <div
                                class="unit-type"
                                v-for="(unitType) in shockUnits"
                                :key="`shock-tactics-unit-type-${unitType}`"
                            >
                                <img
                                    class="unit-coin"
                                    :src="getAssetUrl(`@/assets/units/${unitType.toLowerCase()}.png`)"
                                />
                                <div class="unit-caption">
                                    {{typeToLabel(unitType)}}
                                </div>
                            </div>
                        </div>
                    </li>
                    <li>
                        It also adds a new mechanic called <strong>Shock</strong>:
                        <ul class="items-list">
                            <li>
                                When a unit <strong>shocks</strong> a target unit, take all of the target unit's coins from
                                 the board and place them in its owner's discard pile.
                            </li>
                            <li>
                                Shock is not considered an attack, and therefore does not trigger
                                any of the target unit's attributes (e.g. the Pikeman's attribute will not be triggered).
                            </li>
                        </ul>
                    </li>
                    <li class="sub-caption">
                        And lastly, it introduces 3 new alternative unit types
                         (<span class="link" @click="onToggleAltUnitsModal">unit cards</span>):
                        <div class="unit-types">
                            <div
                                class="unit-type"
                                v-for="(unitType) in alternativeUnits"
                                :key="`alternative-unit-type-${unitType}`"
                            >
                                <img
                                    class="unit-coin"
                                    :src="getAssetUrl(`@/assets/units/${unitType.toLowerCase()}.png`)"
                                />
                                <div class="unit-caption">
                                    {{typeToLabel(unitType)}}
                                </div>
                                <div class="unit-caption alternative">
                                    (Alt for <img
                                        class="unit-coin mini"
                                        :src="getAssetUrl(`@/assets/units/${alternativeUnitTo(unitType).toLowerCase()}.png`)"
                                    />)
                                </div>
                            </div>
                        </div>
                    </li>
                    <li>
                        These units use the original coins and artwork of the Marshal, Lancer,
                         and Footman, but will come with new cards.
                    </li>
                </ul>
            </div>
			<div class="separator" ref="championsExpansionSection">
				<img class="bracket" src="~@/assets/celtic_bracket.png" />
				<span class="section-title">Champions Expansion</span>
				<img class="bracket flipped" src="~@/assets/celtic_bracket.png" />
			</div>
			<div>
				<ul class="items-list">
					<li>
						The Champions expansion adds 4 new champion units
						(<span class="link" @click="onToggleChampionsModal">unit cards</span>):
						<div class="unit-types">
							<div
								class="unit-type"
								v-for="(unitType) in championUnits"
								:key="`champion-unit-type-${unitType}`"
							>
								<img
									class="unit-coin"
									:src="getAssetUrl(`@/assets/units/${unitType.toLowerCase()}.png`)"
								/>
								<div class="unit-caption">
									{{typeToLabel(unitType)}}
								</div>
							</div>
						</div>
					</li>

					<!-- <li>
						<strong>Setup:</strong>
							<ul class="items-list">
								<li>
									Shuffle the 4 champion unit cards.
								</li>
								<li>
									Deal one champion face-up on the table for each player.
								</li>
								<li>
									Add regular Unit cards based on the number of players
									(6 for 2 players, 8 for 4 players).
								</li>
								<li>
									Draft armies from the full display of units.
								</li>
								<li>
									Each player must draft <strong>exactly one champion</strong>.
								</li>
							</ul>
						</li> -->

						<li>
							<strong>Champion Rules:</strong>
							<ul class="items-list">
								<!-- <li>
									Each player has one champion in their army.
								</li> -->
								<li>
									When your champion is destroyed
									(i.e. the last coin in its unit stack is removed from the board),
									your opponent(s) must take one of their control markers
									from their supply and return it to the box.
								</li>
								<li>
									This means they need fewer control markers to win the game.
								</li>
								<li>
									If an opponent has no control markers remaining in their supply,
									they immediately win the game.
								</li>
								<li>
									After a champion is destroyed, it may be redeployed normally.
								</li>
								<li>
									Each time a champion is destroyed, its attribute triggers again.
								</li>
							</ul>
						</li>
					</ul>
			</div>
            <div class="separator">
                <img class="bracket" src="~@/assets/celtic_bracket.png" />
                <span class="section-title">Mastery Expansion (unofficial)</span>
                <img class="bracket flipped" src="~@/assets/celtic_bracket.png" />
            </div>
            <div>
                <ul class="items-list">
                    <li>
                        <div class="item-content">
							     The Mastery expansion adds 4 new units
                             (<span class="link" @click="onToggleMasteryModal">unit cards</span>):
                        </div>
                        <div class="unit-types">
                            <div
                                class="unit-type"
                                v-for="(unitType) in masteryUnits"
                                :key="`mastery-unit-type-${unitType}`"
                            >
                                <img
                                    class="unit-coin"
                                    :src="getAssetUrl(`@/assets/units/${unitType.toLowerCase()}.png`)"
                                />
                                <div class="unit-caption">
                                    {{typeToLabel(unitType)}}
                                </div>
                            </div>
                        </div>
                    </li>
                </ul>
            </div>
			<div class="separator" ref="highSeasExpansionSection">
                <img class="bracket" src="~@/assets/celtic_bracket.png" />
                <span class="section-title">High Seas Expansion (Unofficial)</span>
                <img class="bracket flipped" src="~@/assets/celtic_bracket.png" />
            </div>
            <div>
                <ul class="items-list">
                    <li>
                        <div class="item-content">
                            The High Seas expansion adds 4 new units
                             (<span class="link" @click="onToggleHighSeasModal">unit cards</span>):
                        </div>
                        <div class="unit-types">
                            <div
                                class="unit-type"
                                v-for="(unitType) in highSeasUnits"
                                :key="`shock-tactics-unit-type-${unitType}`"
                            >
                                <img
                                    class="unit-coin"
                                    :src="getAssetUrl(`@/assets/units/${unitType.toLowerCase()}.png`)"
                                />
                                <div class="unit-caption">
                                    {{typeToLabel(unitType)}}
                                </div>
                            </div>
                        </div>
                    </li>
                    <li>
                        It also adds two new mechanics:
                        <ul class="items-list">
							<li>
								<strong>Sea:</strong>
								<ul class="items-list">
									<li>
										The dark tiles on the board are now designated as <strong>Sea</strong> spaces.
									</li>
									<li>
										Certain units (like the Pirate) can access the Sea,
										 including in 2-player games.
									</li>
								</ul>
							</li>
							<li>
								<strong>Sea Tactic:</strong>
								<ul class="items-list">
									<li>
										A <strong>Sea Tactic</strong> acts like a normal tactic,
										but it can <strong>only be activated when a unit is on a Sea space</strong>.
									</li>
									<li>
										Example: The Pirate’s Sea Tactic allows a ranged attack from a Sea position.
									</li>
								</ul>
							</li>
                        </ul>
                    </li>
					<li>
                       Notes on the <strong>Longboat</strong>:
                        <ul class="items-list">
                            <li>
                               A <strong>friendly unit</strong> on the Longboat is <strong>only considered
							   bolstered if it has at least two of its own coins</strong>.
                            </li>
                            <li>
								The Longboat <strong>cannot</strong> use the <strong>March decree</strong>, but the unit
								on the Longboat may use it if it is bolstered.
                            </li>
							<li>
								The Longboat <strong>can be attacked, poisoned, etc.,</strong>
									even while carrying another unit.
							</li>
							<li>
								The Longboat <strong>can always perform a normal attack</strong>, even
								while carrying another unit.
							</li>
							<li>
								When the Longboat moves, it triggers the <strong>movement-based attributes </strong>
								 of any units it's carrying (e.g., Berserker, Bannerman, Sapper).
							</li>
							<li>
								The Longboat can only carry one type of unit at a time.
							</li>
							<li>
								The Longboat, even while carrying another unit, can:
								<ul class="items-list">
									<li>
										Be moved by the <strong>Ensign</strong> or <strong>War Wagon</strong>.
									</li>
									<li>
										Attack via the <strong>Marshal</strong>.
									</li>
								</ul>
								<div class="longboat-example">
									<div class="example-img-wrapper">
										<img class="example-img" src="~@/assets/longboat_select_example_01.png" />
									</div>
									<div class="example-middle">
										<div>
											Click on <img class="longboat-toggle-img" src="~@/assets/longboat_mini_coin.png" />
											 to choose
										</div>
										<div>
											 which unit to activate
										</div>
									</div>
									<div class="example-img-wrapper">
										<img class="example-img" src="~@/assets/longboat_select_example_02.png" />
									</div>
								</div>
							</li>
							<li>
								The Longboat may move under a friendly adjacent unit if it is not carrying a unit.
							</li>
                        </ul>
                    </li>
                </ul>
            </div>
            <div class="separator">
                <img class="bracket" src="~@/assets/celtic_bracket.png" />
                <span class="section-title">Blitz Mode <fa-icon icon="bolt-lightning" class="blitz-icon" /></span>
                <img class="bracket flipped" src="~@/assets/celtic_bracket.png" />
            </div>
            <div>
                <ul class="items-list">
                    <li>
                        <div class="item-content">
                            A new fast-paced mode where each player gets 2 - 5 minutes for the entire game,
                             gains 3 bonus seconds after each turn,
                             and loses automatically if their time ever runs out.
                        </div>
                    </li>
                </ul>
            </div>
        </div>
        <alt-units-modal
            :show="showAltUnitsModal"
            :close="onToggleAltUnitsModal"
        />
        <unit-cards-modal
            type="shock-tactics-units-modal"
            :show="showShockTacticsModal"
            :on-close="onToggleShockTacticsModal"
        >
            <unit-card
                :unit-type="UNIT_TYPE_RAIDER"
                :image-name="'raider.png'"
                :quantity="5"
            />
            <unit-card
                :unit-type="UNIT_TYPE_REARGUARD"
                :image-name="'rearguard.png'"
                :quantity="5"
            />
            <unit-card
                :unit-type="UNIT_TYPE_PITCH_THROWER"
                :image-name="'pitch_thrower.png'"
                :quantity="5"
            />
            <unit-card
                :unit-type="UNIT_TYPE_WAR_DRUMMER"
                :image-name="'war_drummer.png'"
                :quantity="5"
            />
        </unit-cards-modal>
		 <unit-cards-modal
            type="champions-units-modal"
            :show="showChampionsModal"
            :on-close="onToggleChampionsModal"
        >
            <unit-card
                :unit-type="UNIT_TYPE_COMMANDER"
                :image-name="'commander.png'"
                :quantity="5"
            />
            <unit-card
                :unit-type="UNIT_TYPE_DRAGOON"
                :image-name="'dragoon.png'"
                :quantity="5"
            />
            <unit-card
                :unit-type="UNIT_TYPE_MARKSMAN"
                :image-name="'marksman.png'"
                :quantity="5"
            />
            <unit-card
                :unit-type="UNIT_TYPE_RANGER"
                :image-name="'ranger.png'"
                :quantity="5"
            />
        </unit-cards-modal>
		<unit-cards-modal
            type="mastery-units-modal"
            :show="showMasteryModal"
            :on-close="onToggleMasteryModal"
        >
            <unit-card
                :unit-type="UNIT_TYPE_ALCHEMIST"
                :image-name="'alchemist.png'"
                :quantity="5"
            />
            <unit-card
                :unit-type="UNIT_TYPE_APPRENTICE"
                :image-name="'apprentice.png'"
                :quantity="4"
            />
				<unit-card
                :unit-type="UNIT_TYPE_EMISSARY_V2"
                :image-name="'emissary.png'"
                :quantity="5"
            />
            <unit-card
                :unit-type="UNIT_TYPE_OVERLORD"
                :image-name="'overlord.png'"
                :quantity="4"
            />
        </unit-cards-modal>
		<unit-cards-modal
            type="high-seas-units-modal"
            :show="showHighSeasModal"
            :on-close="onToggleHighSeasModal"
        >
            <unit-card
                :unit-type="UNIT_TYPE_PIRATE"
                :image-name="'pirate.png'"
                :quantity="4"
            />
            <unit-card
                :unit-type="UNIT_TYPE_LONGBOAT"
                :image-name="'longboat.png'"
                :quantity="4"
            />
				<unit-card
                :unit-type="UNIT_TYPE_ADMIRAL"
                :image-name="'admiral.png'"
                :quantity="4"
            />
            <unit-card
                :unit-type="UNIT_TYPE_CORSAIR"
                :image-name="'corsair.png'"
                :quantity="5"
            />
        </unit-cards-modal>
    </div>
</template>

<script>
import { getAssetUrl } from '../../helpers/assets';
import controlBaseImage from '../../assets/control_base.svg?url';
import { API_BASE_URL } from '@/helpers/env';
import {
	UNIT_TYPE_ADMIRAL,
	UNIT_TYPE_ALCHEMIST,
	UNIT_TYPE_APPRENTICE,
	UNIT_TYPE_ARCHER,
	UNIT_TYPE_ASSASSIN,
	UNIT_TYPE_BANNERMAN,
	UNIT_TYPE_BERSERKER,
	UNIT_TYPE_BISHOP,
	UNIT_TYPE_CAVALRY,
	UNIT_TYPE_COMMANDER,
	UNIT_TYPE_CORSAIR,
	UNIT_TYPE_CROSSBOWMAN,
	UNIT_TYPE_DECOY_COIN_INFILTRATOR,
	UNIT_TYPE_DECOY_COIN_SKIRMISHER,
	UNIT_TYPE_DRAGOON,
	UNIT_TYPE_EARL,
	UNIT_TYPE_EMISSARY,
	UNIT_TYPE_EMISSARY_V2,
	UNIT_TYPE_ENSIGN,
	UNIT_TYPE_FOOTMAN,
	UNIT_TYPE_HEAVY_CAVALRY,
	UNIT_TYPE_HERALD,
	UNIT_TYPE_INFILTRATOR,
	UNIT_TYPE_KNIGHT,
	UNIT_TYPE_LANCER,
	UNIT_TYPE_LIGHT_CAVALRY,
	UNIT_TYPE_LONGBOAT,
	UNIT_TYPE_MARKSMAN,
	UNIT_TYPE_MARSHAL,
	UNIT_TYPE_MERCENARY,
	UNIT_TYPE_OVERLORD,
	UNIT_TYPE_PIKEMAN,
	UNIT_TYPE_PIRATE,
	UNIT_TYPE_PITCH_THROWER,
	UNIT_TYPE_RAIDER,
	UNIT_TYPE_RANGER,
	UNIT_TYPE_REARGUARD,
	UNIT_TYPE_ROYAL_COIN_BLACK,
	UNIT_TYPE_ROYAL_COIN_WHITE,
	UNIT_TYPE_ROYAL_GUARD,
	UNIT_TYPE_ROYAL_GUARD_NOBILITY,
	UNIT_TYPE_SABOTEUR,
	UNIT_TYPE_SAPPER,
	UNIT_TYPE_SCOUT,
	UNIT_TYPE_SIEGE_TOWER,
	UNIT_TYPE_SKIRMISHER,
	UNIT_TYPE_SWORDSMAN,
	UNIT_TYPE_TREBUCHET,
	UNIT_TYPE_VANGUARD,
	UNIT_TYPE_WAR_DRUMMER,
	UNIT_TYPE_WAR_WAGON,
	UNIT_TYPE_WARLORD,
	UNIT_TYPE_WARRIOR_PRIEST,
} from '../Unit/Unit-types';
import ArrowTurnUp from '../../assets/arrow_turn_up.svg';
import ShockIcon from '../../assets/shock_icon.svg';

export default {
    name: 'Rules',
	components: {
		ArrowTurnUp,
		ShockIcon,
	},
    async mounted() {
        let { scrollTo } = this.$route.query;

        if (scrollTo === 'nightfall') {
            setTimeout(() => {
                window.scroll({
                    top: this.$refs['nightfallExpansionSection'].getBoundingClientRect().top,
                    behavior: 'smooth',
                });
            }, 250);
        }

        if (scrollTo === 'shockTactics') {
            setTimeout(() => {
                window.scroll({
                    top: this.$refs['shockTacticsExpansionSection'].getBoundingClientRect().top,
                    behavior: 'smooth',
                });
            }, 250);
        }

		if (scrollTo === 'champions') {
            setTimeout(() => {
                window.scroll({
                    top: this.$refs['championsExpansionSection'].getBoundingClientRect().top,
                    behavior: 'smooth',
                });
            }, 250);
        }

		if (scrollTo) {
			setTimeout(() => {
				this.$router.replace({
					query: {
						...this.$route.query,
						scrollTo: undefined,
					},
				});
			}, 1500);
		}
    },
    data() {
        return {
			controlBaseImage,
			['UNIT_TYPE_ADMIRAL']: UNIT_TYPE_ADMIRAL,
			['UNIT_TYPE_ALCHEMIST']: UNIT_TYPE_ALCHEMIST,
			['UNIT_TYPE_APPRENTICE']: UNIT_TYPE_APPRENTICE,
			['UNIT_TYPE_ARCHER']: UNIT_TYPE_ARCHER,
			['UNIT_TYPE_BANNERMAN']: UNIT_TYPE_BANNERMAN,
			['UNIT_TYPE_BERSERKER']: UNIT_TYPE_BERSERKER,
			['UNIT_TYPE_BISHOP']: UNIT_TYPE_BISHOP,
			['UNIT_TYPE_CAVALRY']: UNIT_TYPE_CAVALRY,
			['UNIT_TYPE_COMMANDER']: UNIT_TYPE_COMMANDER,
			['UNIT_TYPE_CORSAIR']: UNIT_TYPE_CORSAIR,
			['UNIT_TYPE_CROSSBOWMAN']: UNIT_TYPE_CROSSBOWMAN,
			['UNIT_TYPE_DECOY_COIN_INFILTRATOR']: UNIT_TYPE_DECOY_COIN_INFILTRATOR,
			['UNIT_TYPE_DECOY_COIN_SKIRMISHER']: UNIT_TYPE_DECOY_COIN_SKIRMISHER,
			['UNIT_TYPE_DRAGOON']: UNIT_TYPE_DRAGOON,
			['UNIT_TYPE_EARL']: UNIT_TYPE_EARL,
			['UNIT_TYPE_EMISSARY_V2']: UNIT_TYPE_EMISSARY_V2,
			['UNIT_TYPE_ENSIGN']: UNIT_TYPE_ENSIGN,
			['UNIT_TYPE_FOOTMAN']: UNIT_TYPE_FOOTMAN,
			['UNIT_TYPE_HEAVY_CAVALRY']: UNIT_TYPE_HEAVY_CAVALRY,
			['UNIT_TYPE_HERALD']: UNIT_TYPE_HERALD,
			['UNIT_TYPE_KNIGHT']: UNIT_TYPE_KNIGHT,
			['UNIT_TYPE_LANCER']: UNIT_TYPE_LANCER,
			['UNIT_TYPE_LIGHT_CAVALRY']: UNIT_TYPE_LIGHT_CAVALRY,
			['UNIT_TYPE_LONGBOAT']: UNIT_TYPE_LONGBOAT,
			['UNIT_TYPE_OVERLORD']: UNIT_TYPE_OVERLORD,
			['UNIT_TYPE_MARSHAL']: UNIT_TYPE_MARSHAL,
			['UNIT_TYPE_MARKSMAN']: UNIT_TYPE_MARKSMAN,
			['UNIT_TYPE_MERCENARY']: UNIT_TYPE_MERCENARY,
			['UNIT_TYPE_PIKEMAN']: UNIT_TYPE_PIKEMAN,
			['UNIT_TYPE_PIRATE']: UNIT_TYPE_PIRATE,
			['UNIT_TYPE_PITCH_THROWER']: UNIT_TYPE_PITCH_THROWER,
			['UNIT_TYPE_RAIDER']: UNIT_TYPE_RAIDER,
			['UNIT_TYPE_RANGER']: UNIT_TYPE_RANGER,
			['UNIT_TYPE_REARGUARD']: UNIT_TYPE_REARGUARD,
			['UNIT_TYPE_ROYAL_COIN_BLACK']: UNIT_TYPE_ROYAL_COIN_BLACK,
			['UNIT_TYPE_ROYAL_COIN_WHITE']: UNIT_TYPE_ROYAL_COIN_WHITE,
			['UNIT_TYPE_ROYAL_GUARD']: UNIT_TYPE_ROYAL_GUARD,
			['UNIT_TYPE_ROYAL_GUARD_NOBILITY']: UNIT_TYPE_ROYAL_GUARD_NOBILITY,
			['UNIT_TYPE_SCOUT']: UNIT_TYPE_SCOUT,
			['UNIT_TYPE_SWORDSMAN']: UNIT_TYPE_SWORDSMAN,
			['UNIT_TYPE_VANGUARD']: UNIT_TYPE_VANGUARD,
			['UNIT_TYPE_WAR_DRUMMER']: UNIT_TYPE_WAR_DRUMMER,
			['UNIT_TYPE_WARLORD']: UNIT_TYPE_WARLORD,
			['UNIT_TYPE_WARRIOR_PRIEST']: UNIT_TYPE_WARRIOR_PRIEST,

            actionTypes: [
                'deploy',
                'bolster',
                'claim_initiative',
                'recruit',
                'pass',
                'move',
                'attack',
                'control',
                'attack_via_position',
            ],
            masteryUnits: [
                UNIT_TYPE_ALCHEMIST,
                UNIT_TYPE_APPRENTICE,
                UNIT_TYPE_EMISSARY,
                UNIT_TYPE_OVERLORD,
            ],
            nobilityUnits: [
                UNIT_TYPE_BANNERMAN,
                UNIT_TYPE_BISHOP,
                UNIT_TYPE_EARL,
                UNIT_TYPE_HERALD,
            ],
            siegeUnits: [
                UNIT_TYPE_SAPPER,
                UNIT_TYPE_SIEGE_TOWER,
                UNIT_TYPE_TREBUCHET,
                UNIT_TYPE_WAR_WAGON,
            ],
            nightfallUnits: [
                UNIT_TYPE_ASSASSIN,
                UNIT_TYPE_SABOTEUR,
                UNIT_TYPE_INFILTRATOR,
                UNIT_TYPE_SKIRMISHER,
            ],
            shockUnits: [
                UNIT_TYPE_PITCH_THROWER,
                UNIT_TYPE_RAIDER,
                UNIT_TYPE_REARGUARD,
                UNIT_TYPE_WAR_DRUMMER,
            ],
			highSeasUnits: [
                UNIT_TYPE_PIRATE,
                UNIT_TYPE_LONGBOAT,
                UNIT_TYPE_CORSAIR,
				UNIT_TYPE_ADMIRAL,
            ],
			championUnits: [
				UNIT_TYPE_COMMANDER,
				UNIT_TYPE_DRAGOON,
				UNIT_TYPE_MARKSMAN,
				UNIT_TYPE_RANGER,
			],
            alternativeUnits: [
                UNIT_TYPE_VANGUARD,
                UNIT_TYPE_HEAVY_CAVALRY,
                UNIT_TYPE_WARLORD,
            ],
            fortLayoutExample: [
                { position: '4,0' },
                { position: '2,1' },
                { position: '4,5' },
                { position: '2,6' },
            ],
            showAltUnitsModal: false,
            showShockTacticsModal: false,
			showMasteryModal: false,
            showHighSeasModal: false,
			showChampionsModal: false,
        };
    },
    computed: {
        assetsUrl() {
            return `${API_BASE_URL}/assets`;
        },
        userId() {
            return this.$store.state.loginState.userId;
        },
    },
    methods: {
        getAssetUrl,
        alternativeUnitTo(unitType) {
            switch (unitType) {
                case UNIT_TYPE_HEAVY_CAVALRY:
                    return UNIT_TYPE_LANCER;
                case UNIT_TYPE_VANGUARD:
                    return UNIT_TYPE_FOOTMAN;
                case UNIT_TYPE_WARLORD:
                    return UNIT_TYPE_MARSHAL;
                default:
                    return '';
            }
        },
        imgUrl(unitType) {
            return `${this.assetsUrl}/${unitType.toLowerCase()}.png`;
        },
        onToggleAltUnitsModal() {
            this.showAltUnitsModal = !this.showAltUnitsModal;
        },
        onToggleShockTacticsModal() {
            this.showShockTacticsModal = !this.showShockTacticsModal;
        },
		onToggleChampionsModal() {
            this.showChampionsModal = !this.showChampionsModal;
        },
		onToggleHighSeasModal() {
            this.showHighSeasModal = !this.showHighSeasModal;
        },
		onToggleMasteryModal() {
            this.showMasteryModal = !this.showMasteryModal;
        },
        typeToLabel(text) {
            return text.split('_').map(word =>
                `${word.slice(0, 1).toUpperCase()}${word.slice(1).toLowerCase()}`,
            ).join(' ');
        },
    },
};
</script>

<style scoped lang="scss">
    @use "@/styles/styles.scss" as *;

    .rules {
        margin: 0 auto;

        i {
            font-style: italic;
        }

        &.content-wrapper {
            background: $off-white;
            border-radius: 4px;
            box-sizing: border-box;
            font-family: 'Avenir', 'RobotoSlab', Helvetica, Arial, sans-serif;
        }

        .action-icon-placeholder {
            margin-right: 15px;
            max-width: 35px;
            min-width: 35px;
        }

        .btn-icon-group,
        .btn-icon {
            box-sizing: border-box;
            display: inline-flex;
            height: 35px;
            margin-right: 15px;
            min-height: 35px;
            min-width: 35px;
            padding: 7px;
            position: relative;
            vertical-align: middle;
            width: 35px;

            .action-icon {
                height: auto;
                max-width: 10px;

                &.poison,
                &.shock {
                    max-width: 15px;
                }
            }

            .action-icon-secondary {
                margin-left: 2px;
                min-height: 12px;
                min-width: 12px;
            }

            &.attack {

                .action-icon {
                    height: 24px;
                    max-width: none;
                    width: 24px;
                }

                &.sacrifice-attack {

                    .action-icon {

                        &.drop {
                            position: absolute;
                            width: 9px;
                            height: 9px;
                            right: 3px;
                            top: 14px;
                        }
                    }
                }
            }

            &.bolster {

                .action-icon {
                    height: 24px;
                    max-width: none;
                    width: 24px;

                    &.plus-icon {
                        color: $dark-blue;
                        left: 50%;
                        max-height: 40%;
                        max-width: 40%;
                        position: absolute;
                        top: 50%;
                        transform: translate(-50%, -50%);
                    }
                }
            }

			&.unbolster {

                .action-icon {
                    height: 24px;
                    max-width: none;
                    width: 24px;

                    &.minus-icon {
                        color: $dark-blue;
                        left: 50%;
                        max-height: 40%;
                        max-width: 40%;
                        position: absolute;
                        top: 50%;
                        transform: translate(-50%, -50%);
                    }
                }
            }

			&.maneuver {

                .action-icon {
                    height: 24px;
                    max-width: none;
                    width: 24px;
                }

				.shock-icon {
					right: 13%;
					max-height: 20%;
					max-width: 20%;
					position: absolute;
					top: 18%;
				}
            }


            &.build-fort {
                padding: 2px;

                .action-icon {

                    &.fort-icon {
                        max-width: 15px;
                    }
                }

                .plus-icon {
                    height: 7px;
                    margin-right: 2px;
                    margin-left: -1px;
                    width: 7px;
                }
            }

            &.attack-fort {
                padding: 2px;

                .action-icon {

                    &.fort-icon {
                        max-width: 15px;
                    }
                }

                .action-icon-secondary {
                    position: absolute;
                    height: 75%;
                    transform: rotate(-45deg);

                    .action-icon-slash {
                        background: white;
                        border-radius: 1px;
                        display: block;
                        height: 100%;
                        left: 50%;
                        position: absolute;
                        top: 50%;
                        transform: translate(-50%, -50%);
                        width: 2px;

                        &:nth-of-type(2) {
                            background: $dark-blue;
                            left: calc(50% - 2px);
                        }
                    }
                }
            }
        }

        .btn-icon {
            box-sizing: border-box;
            height: 35px;
            padding: 7px;
            width: 35px;

            &.horseshoe {
                height: 35px;
                min-width: 35px;
                padding: 3px;
                padding-top: 6px;
            }
        }

        .content {
            border-radius: 5px;
            color: $dark-gray;
            font-size: 1.1em;


			.longboat-example {
				display: flex;
				justify-content: center;
				align-items: center;
				margin-top: 15px;

				.longboat-toggle-img {
					max-width: 35px;
					max-height: 35px;
					vertical-align: middle;
					margin: 0 5px;
				}

				.example-img-wrapper {
					max-width: 150px;
					max-height: 150px;
					margin: 0 15px;

					.example-img {
						width: 100%;
						height: auto;
					}
				}

				.example-middle {
					display: flex;
					flex-direction: column;
					align-items: center;
					justify-content: center;

					> * {
						margin: 3.5px auto;
					}
				}
			}

            .actions-gallery {
                display: flex;
                flex-wrap: wrap;
                justify-content: space-evenly;
                margin-top: 35px;

                .actions-gallery-item {
                    display: inline-block;
                    text-align: center;
                    margin-bottom: 10px;

                    .action-img-wrapper {
                        background: $cream;
                        border-radius: 5px;
                        box-shadow: -1px 1px 2px 1px $silver;
                        min-width: 130px;
                        padding: 12px 8px;

                        .action-img {
                            display: block;
                            height: auto;
                            margin: 0 auto;
                            max-height: 125px;
                            max-width: 125px;
                            width: auto;
                        }
                    }

                    .caption {
                        display: inline-block;
                    }
                }
            }

            .caption {
                color: $dark-gray;
                font-size: .8em;
                font-weight: 300;
                margin-top: 7px;
                text-align: center;
            }

            .items-list {
                list-style-type: disc !important; // overtrides the default ul list style type of "none"
                margin-left: 25px;

                &.actions-list {
                    list-style-type: none;
                    margin-left: 0;
                }

                li {
                    margin-bottom: 15px;

                    .subtitle {

                        > * {
                            line-height: 25px;
                            vertical-align: middle;
                        }

						.subtitle-label {
							margin-left: 4px;
						}
                    }

                    .description {
                        margin-top: 7.5px;
                    }

                    .item-content {
                        display: flex;
                    }

                    .text-grouping {
                        display: flex;
                    }

                    .coin-image,
                    .token-image {
                        align-self: center;
                        height: auto;
                        max-height: 25px;
                        max-width: 25px;
                        width: auto;
                        vertical-align: middle;
                    }

                    .token-image  {
                        margin-right: 4px;
                    }

                    .text {
                        vertical-align: middle;
                        align-self: center;
                    }

                    ul {
                        list-style-type: none;
                        margin-top: 10px;
                    }

                    .special-note {
                        background: $extra-light-gray;
                        border-radius: 4px;
                        color: $dark-gray-alt;
                        display: inline-block;
                        font-size: .75em;
                        font-style: italic;
                        margin-top: 10px;
                        padding: 8px;
                    }
                }
            }

            .quote {
                background: $off-white;
                box-shadow: 1px 1px 3px 2px $light-gray;
                font-style: italic;
                padding: 20px;
                border-radius: 5px;
                font-size: .9em;
                margin: 0 auto;
                margin-bottom: 65px;
                position: relative;

                .quote-attribution {
                    bottom: -30px;
                    color: $dark-gray;
                    position: absolute;
                    right: 10px;
                }
            }

            .video {
                text-align: center;

                iframe {
                    width: 100%;
                    height: 366px;
                }
            }

            .visual {
                padding: 20px 0 5px 0;

                .arrow {
                    display: inline-block;
                    margin: auto 10px;
                    vertical-align: middle;

                    &.right {

                        .arrow-icon {
                            transform: rotate(-90deg);
                        }
                    }


                    &.left {

                        .arrow-icon {
                            transform: rotate(90deg);
                        }
                    }
                }

                .example-unit {
                    margin-right: 10px;
                    max-height: 50px;
                    max-width: 50px;
                    vertical-align: middle;

                    &:last-of-type {
                        margin-right: 0;
                    }

                    &.last-coin {
                        margin-right: 0;
                    }
                }

                .example-unit-group {
                    margin-right: 10px;
                    position: relative;
                    display: inline-block;
                    vertical-align: middle;

                    &.last-group {
                        margin-right: 0;
                    }

                    .example-unit {
                        margin-right: 0;

                        &:nth-last-of-type(2) {
                            left: 2.5px;
                            position: absolute;
                            top: -6.5px;
                        }
                    }
                }

                .bag-container {
                    display: inline-block;

                    .bag {
                        height: 75px;
                        width: auto;
                        vertical-align: middle;
                    }
                }

                .initiative-wrapper {
                    background: $cream;
                    display: inline-flex;
                    padding: 8px 12px;
                    border-radius: 5px;
                    box-shadow: -1px 1px 2px 1px $silver;

                    .initiative-text {
                        vertical-align: middle;
                        align-self: center;
                        margin-right: 7px;
                    }

                    .royal-coin {
                        background: $cream;
                        border-radius: 5px;
                        box-shadow: -1px 1px 2px 1px $silver;
                        display: inline-flex;
                        padding: 8px 12px;
                    }
                }

                .control-marker-group {
                    display: inline-block;
                    text-align: center;

                    .control-marker-images {
                        border-radius: 5px;
                        display: inline-block;

                        .control-marker {
                            height: 60px;
                            display: inline-block;
                            width: 60px;

                            &:not(:last-of-type) {
                                margin-right: 8.5px;
                            }
                        }
                    }

                    &:not(:last-of-type) {
                        margin-right: 25px;
                    }
                }

                .tiles {

                    .tile-wrapper {
                        display: inline-block;

                        .tile-image-wrapper {
                            position: relative;
                            text-align: center;

                            .control-base {
                                height: 52px;
                                left: 50%;
                                position: absolute;
                                top: 50%;
                                transform: translate(-50%, -50%);
                                width: 52px;
                            }
                        }

                        &:not(:last-of-type) {
                            margin-right: 15px;
                        }

                        .rules-tile {
                            height: auto;
                            max-height: 75px;
                            max-width: 75px;
                            vertical-align: middle;
                            width: auto;

                            &.has-control-base {
                                margin-right: 0;
                            }
                        }
                    }
                }

                .action-img {
                    height: auto;
                    max-height: 115px;
                    max-width: 115px;
                    width: auto;
                }
            }

            .unit-types {
                margin-top: 10px;

                .unit-type {
                    display: inline-flex;
                    flex-direction: column;
                    justify-content: center;
                    text-align: center;
                    margin: 0 10px 10px 0;

                    &:last-of-type {
                        margin-right: 0;
                    }

                    .unit-coin {
                        align-self: center;
                        height: auto;
                        max-height: 65px;
                        max-width: 65px;
                        width: auto;

                        &.mini {
                            max-height: 20px;
                            max-width: 20px;
                            margin-left: 3px;
                        }
                    }

                    .unit-caption {
                        align-self: center;
                        color: $dark-blue;
                        font-size: .8em;
                        margin-top: 5px;
                        display: flex;
                        align-items: center;

                        &.alternative {
                            font-size: .7em;
                        }
                    }
                }
            }

            .decrees {
                margin-top: 10px;

                .decree-name {
                    background: $light-gray;
                    border-radius: 5px;
                    display: inline-block;
                    margin: 0 10px 10px 0;
                    padding: 4px 6px;

                    &:last-of-type {
                        margin-left: 0;
                    }
                }
            }

            .fort-layout-example {
                margin-top: 10px;
            }

            .link-wrapper {
                height: 15px;
                position: relative;

                .link {
                    left: 50%;
                    position: absolute;
                    text-align: center;
                    top: -19px;
                    transform: translateX(-50%);
                    width: 100%;
                }
            }
        }

        .separator {
            display: flex;
            justify-content: center;
            margin-top: 30px;

            > * {
                align-self: center;
            }

            .section-title {

                .blitz-icon {
                    color: $herald;
                    font-size: 1.2rem;
                }
            }
        }
    }

    .modal-rules {

        .rules {

            .content {
                padding-bottom: 20px;
            }
        }
    }



    @media only screen and (min-width: $screen-xs) {

        .rules {

            .content {

                .actions-gallery {

                    .actions-gallery-item {
                        margin-right: 35px;

                        .action-img-wrapper {
                            min-width: 150px;

                            .action-img {
                                max-width: 200px;
                            }
                        }
                    }
                }

                .decrees {

                    .decree {
                        margin-bottom: 0;
                    }
                }

                .unit-types {

                    .unit-type {
                        margin-bottom: 0;
                    }
                }
            }

			.champions-units-modal,
            .shock-tactics-units-modal {

                .unit-cards {
					overflow: scroll;
                    flex-direction: row;
                }
            }
        }
    }

    @media only screen and (min-width: $screen-sm) {

        .rules {

            &.content-wrapper {
                margin-top: 25px;
            }

            .content {

                .quote {
                    max-width: 75%;
                }

                .video {

                    iframe {
                        width: 650px;
                    }
                }
            }

            .title-wrapper {

                .title {
                    font-size: 3.5em;
                    max-width: none;
                }
            }
        }
    }

</style>
